import {clsx} from 'clsx'
import type {ChildrenProps} from './sidebar-context'
import {sidebarChildKind} from './contains-current'
import styles from './Section.module.css'
import type {SectionGroupProps} from './SectionGroup'
import {SectionGroup} from './SectionGroup'

type BaseSectionProps = ChildrenProps & {
  /** Draws a divider above the section. Commonly used for footer sections. */
  divider?: boolean
}

type UntitledSectionProps = BaseSectionProps & {} & {
  [Key in Exclude<keyof SectionGroupProps, 'children'>]?: never
}

type TitledSectionProps = SectionGroupProps & BaseSectionProps

export type SectionProps = UntitledSectionProps | TitledSectionProps

const SectionContainer = Object.assign(
  function SidebarSection({children, divider = false}: SectionProps) {
    return (
      <div className={clsx(styles.Section, divider && styles.divider)} data-component="Sidebar.Section">
        {children}
      </div>
    )
  },
  {[sidebarChildKind]: 'container' as const},
)

export const Section = Object.assign(
  function SidebarSection(props: SectionProps) {
    const isUntitled = props.title === undefined && props.icon === undefined

    const isInvalidGroup =
      !isUntitled && (props.title === undefined || props.title.trim() === '' || props.icon === undefined)

    if (process.env.NODE_ENV !== 'production' && isInvalidGroup) {
      // eslint-disable-next-line no-console
      console.warn('[Sidebar.Section] Titled sections require a non-empty title and an icon.')
    }

    if (isUntitled) {
      return <SectionContainer divider={props.divider}>{props.children}</SectionContainer>
    }

    const {divider, ...groupProps} = props

    return (
      <SectionContainer divider={divider}>
        <SectionGroup {...groupProps} />
      </SectionContainer>
    )
  },
  {[sidebarChildKind]: 'container' as const},
)
